import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast, Toaster } from 'react-hot-toast';
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (num) => Number(num || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// ─── Color Concept ────────────────────────────────────────────────────────────
// 🟢 GREEN  = Main Section headers (auto-calculated totals, read-only)
// 🟡 YELLOW = Sub-category headers (titles like "1. Revenue Direct")
// ⬜ WHITE  = Dynamic / editable input rows (user enters amounts)
// ─────────────────────────────────────────────────────────────────────────────

// ─── SECTION STRUCTURE (from Bank Project Report Database.xlsx) ──────────────
const DEFAULT_STRUCTURE = [
  // ── SECTION I: INCOME AND EXPENDITURE ──────────────────────────────────────
  { ref_id: 'revenue_direct', main: 'INCOME AND EXPENDITURE', category: 'Income', title: 'Revenue Direct', items: ['Direct Sales / Gross Receipts'] },
  { ref_id: 'revenue_others', main: 'INCOME AND EXPENDITURE', category: 'Income', title: 'Revenue: Others', items: ['Interest on Investments'] },
  { ref_id: 'stock_on_hand', main: 'INCOME AND EXPENDITURE', category: 'Income', title: 'Stock on Hand', items: ['Closing Stock'] },

  { ref_id: 'opening_stock', main: 'INCOME AND EXPENDITURE', category: 'Expenditure', title: 'Opening Stock', items: ['Opening Stock'] },
  { ref_id: 'purchases', main: 'INCOME AND EXPENDITURE', category: 'Expenditure', title: 'Purchases', items: ['Purchases'] },
  {
    ref_id: 'other_direct_expenses', main: 'INCOME AND EXPENDITURE', category: 'Expenditure', title: 'Other Direct Expenses', items: [
      'Salaries and Wages', 'Staff Welfare', 'Staff Recruitment and Training', 'Lease Expenses - Buildings',
      'Repairs and Maintenance & AMC', 'Travelling and Conveyance', 'Marketing and Promotional Exp.',
      'Printing and Stationery', 'Insurance for Machinery', 'Insurance - Staff', 'Legal & Admin Expenses', 'Accounting and Audit'
    ]
  },
  { ref_id: 'interest_payments', main: 'INCOME AND EXPENDITURE', category: 'Expenditure', title: 'Interest Payments', items: ['Interest Payments'] },
  { ref_id: 'depreciation', main: 'INCOME AND EXPENDITURE', category: 'Expenditure', title: 'Depreciation', items: ['Depreciation'] },

  // ── SECTION II: BALANCE SHEET ───────────────────────────────────────────────
  { ref_id: 'partners_account', main: 'BALANCE SHEET', category: 'Capital and Liabilities', title: 'Partners Account', items: ['Capital A/c of Partners', 'Reserves and Surplus'] },
  { ref_id: 'secured_loans', main: 'BALANCE SHEET', category: 'Capital and Liabilities', title: 'Secured Loans', items: ['Term Loan from Bank', 'Bank OD Limit'] },
  { ref_id: 'current_liabilities', main: 'BALANCE SHEET', category: 'Capital and Liabilities', title: 'Current Liabilities', items: ['Sundry Creditors', 'Provisions'] },

  { ref_id: 'fixed_assets', main: 'BALANCE SHEET', category: 'Properties and Assets', title: 'Fixed Assets', items: ['Building and Civil Structures', 'Equipments, Plant and Machinery', 'Other Facilities'] },
  { ref_id: 'investments', main: 'BALANCE SHEET', category: 'Properties and Assets', title: 'Investments', items: ['Investments for Expansion'] },
  { ref_id: 'current_assets', main: 'BALANCE SHEET', category: 'Properties and Assets', title: 'Current Assets', items: ['Lease Advance', 'Stock of Inventories', 'Sundry Receivables', 'Deposits & Advances', 'Cash and Cash Equivalents'] },

  // ── SECTION III: DEPRECIATION SCHEDULE ─────────────────────────────────────
  {
    ref_id: 'depr_buildings', main: 'DEPRECIATION SCHEDULE', category: 'Buildings and Civil Structure', title: 'Buildings and Civil Structure', items: [
      'Cost / Written Down Value', 'Depreciation @ 10%', 'Closing Written Down Value'
    ]
  },
  {
    ref_id: 'depr_equipment', main: 'DEPRECIATION SCHEDULE', category: 'Equipments, Plant and Machinery', title: 'Equipments, Plant and Machinery', items: [
      'Cost / Written Down Value', 'Depreciation @ 15%', 'Closing Written Down Value'
    ]
  },
  {
    ref_id: 'depr_computers', main: 'DEPRECIATION SCHEDULE', category: 'Computers', title: 'Computers', items: [
      'Cost / Written Down Value', 'Depreciation @ 25%', 'Closing Written Down Value'
    ]
  },

  // ── SECTION IV: CASH FLOW STATEMENTS ────────────────────────────────────────
  {
    ref_id: 'cash_inflow', main: 'CASH FLOW STATEMENTS', category: 'Inflow of Funds', title: 'Inflow of Funds', items: [
      'Share Capital', 'Term Loan', 'Working Capital Facility', 'Net Surplus for the year',
      'Amortisation of Pre-op Exp.', 'Depreciation Reversed'
    ]
  },
  {
    ref_id: 'cash_outflow', main: 'CASH FLOW STATEMENTS', category: 'Outflow of Funds', title: 'Outflow of Funds', items: [
      'Building and Civil Structures', 'Equipments, Plant & Machinery', 'Computers',
      'Deposits and Advances', 'Pre-operative Exp. & Contingencies', 'Investments for Expansion',
      'Increase in Current Assets', 'Repayment of Other Loans', 'Repayment of Term Loan'
    ]
  },
  {
    ref_id: 'cash_summary', main: 'CASH FLOW STATEMENTS', category: 'Cash Summary', title: 'Cash Summary', items: [
      'Net flow of Cash', 'Cash - Opening Balance', 'Cash - Closing Balance'
    ]
  },

  // ── SECTION V: DEBT SERVICE COVERAGE RATIO ──────────────────────────────────
  {
    ref_id: 'dscr_cash_accruals', main: 'DEBT SERVICE COVERAGE RATIO', category: 'Total Cash Accruals', title: 'Total Cash Accruals', items: [
      'Net Surplus Available', 'Depreciation', 'Principal Repayments', 'Interest Payments'
    ]
  },
  {
    ref_id: 'dscr_obligations', main: 'DEBT SERVICE COVERAGE RATIO', category: 'Total Obligations', title: 'Total Obligations', items: [
      'Principal Repayments', 'Interest Payments'
    ]
  },
];

// ─── Section theme config ─────────────────────────────────────────────────────
const SECTION_THEMES = {
  'INCOME AND EXPENDITURE': { icon: 'I', border: 'border-emerald-500/40', bg: 'bg-emerald-950', headerBg: 'bg-emerald-500/15', iconBg: 'bg-emerald-500/20', iconText: 'text-emerald-400', titleText: 'text-emerald-300' },
  'BALANCE SHEET': { icon: 'II', border: 'border-cyan-500/40', bg: 'bg-cyan-950', headerBg: 'bg-cyan-500/15', iconBg: 'bg-cyan-500/20', iconText: 'text-cyan-400', titleText: 'text-cyan-300' },
  'DEPRECIATION SCHEDULE': { icon: 'III', border: 'border-violet-500/40', bg: 'bg-violet-950', headerBg: 'bg-violet-500/15', iconBg: 'bg-violet-500/20', iconText: 'text-violet-400', titleText: 'text-violet-300' },
  'CASH FLOW STATEMENTS': { icon: 'IV', border: 'border-amber-500/40', bg: 'bg-amber-950', headerBg: 'bg-amber-500/15', iconBg: 'bg-amber-500/20', iconText: 'text-amber-400', titleText: 'text-amber-300' },
  'DEBT SERVICE COVERAGE RATIO': { icon: 'V', border: 'border-rose-500/40', bg: 'bg-rose-950', headerBg: 'bg-rose-500/15', iconBg: 'bg-rose-500/20', iconText: 'text-rose-400', titleText: 'text-rose-300' },
};

// Category accent colors within section
const CATEGORY_COLORS = {
  'Income': { header: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300', total: 'text-emerald-300', focus: 'focus:border-emerald-500' },
  'Expenditure': { header: 'bg-rose-500/20 border-rose-500/40 text-rose-300', total: 'text-rose-300', focus: 'focus:border-rose-500' },
  'Capital and Liabilities': { header: 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300', total: 'text-cyan-300', focus: 'focus:border-cyan-500' },
  'Properties and Assets': { header: 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300', total: 'text-indigo-300', focus: 'focus:border-indigo-500' },
  'Buildings and Civil Structure': { header: 'bg-violet-500/20 border-violet-500/40 text-violet-300', total: 'text-violet-300', focus: 'focus:border-violet-500' },
  'Equipments, Plant and Machinery': { header: 'bg-purple-500/20 border-purple-500/40 text-purple-300', total: 'text-purple-300', focus: 'focus:border-purple-500' },
  'Computers': { header: 'bg-fuchsia-500/20 border-fuchsia-500/40 text-fuchsia-300', total: 'text-fuchsia-300', focus: 'focus:border-fuchsia-500' },
  'Inflow of Funds': { header: 'bg-green-500/20 border-green-500/40 text-green-300', total: 'text-green-300', focus: 'focus:border-green-500' },
  'Outflow of Funds': { header: 'bg-orange-500/20 border-orange-500/40 text-orange-300', total: 'text-orange-300', focus: 'focus:border-orange-500' },
  'Cash Summary': { header: 'bg-amber-500/20 border-amber-500/40 text-amber-300', total: 'text-amber-300', focus: 'focus:border-amber-500' },
  'Total Cash Accruals': { header: 'bg-teal-500/20 border-teal-500/40 text-teal-300', total: 'text-teal-300', focus: 'focus:border-teal-500' },
  'Total Obligations': { header: 'bg-rose-500/20 border-rose-500/40 text-rose-300', total: 'text-rose-300', focus: 'focus:border-rose-500' },
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FinancialStatement = ({ isEmbedded, selectedReportId }) => {

  const [initialStructure, setInitialStructure] = useState([]);
  const [mainSections, setMainSections] = useState([]);
  const [categoriesInSection, setCategoriesInSection] = useState({});
  const [reportsList, setReportsList] = useState([]);
  const [reportId, setReportId] = useState(null);
  const [reportName, setReportName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [openSections, setOpenSections] = useState(
    Object.fromEntries(mainSections.map(s => [s, true]))
  );

  // ── Client filter state ───────────────────────────────────────────────────
  const [clientsList, setClientsList] = useState([]);   // clients with templates
  const [selectedClientId, setSelectedClientId] = useState('');

  // ── Tab system ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('financial'); // 'financial' | 'percentage'
  const [fyPercentages, setFyPercentages] = useState([]);
  const [selectedFYTab, setSelectedFYTab] = useState('');
  const [fyLoading, setFyLoading] = useState(false);

  // Fetch FY percentages for this report
  const fetchFYPercentages = async (rId) => {
    if (!rId) { setFyPercentages([]); return; }
    setFyLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/project-percentage`, {
        params: { report_id: rId }, withCredentials: true
      });
      setFyPercentages(res.data);
      // Auto-select most recent FY
      const fys = [...new Set(res.data.map(r => r.financial_year))].sort().reverse();
      setSelectedFYTab(fys[0] || '');
    } catch { setFyPercentages([]); }
    finally { setFyLoading(false); }
  };

  const fyList = [...new Set(fyPercentages.map(r => r.financial_year))].sort().reverse();
  const fyDisplayed = selectedFYTab ? fyPercentages.filter(r => r.financial_year === selectedFYTab) : fyPercentages;

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };


  useEffect(() => {
    fetchReports();
    // Fetch only clients that have a template assigned
    axios.get(`${process.env.REACT_APP_API_URL}/api/clients/with-template`, { withCredentials: true })
      .then(res => setClientsList(res.data))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (isEmbedded && selectedReportId) {
      loadReport(selectedReportId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, selectedReportId]);

  // ── Initialize empty data structure ──────────────────────────────────────
  const initEmptyData = () => {
    const empty = {};
    initialStructure.forEach(sec => {
      empty[sec.ref_id] = sec.items.map(itemObj => {
        const staticItemName = typeof itemObj === 'string' ? itemObj : itemObj.name;
        const staticItemId = typeof itemObj === 'object' ? itemObj.id : null;
        return {
          item_name: staticItemName,
          item_id: staticItemId,
          amount: 0,
          is_static: true,
          main_section: sec.main,
          category: sec.category,
          sub_category: sec.title,
          main_section_id: sec.main_id || null,
          category_id: sec.category_id || null,
          sub_category_id: sec.sub_category_id || null
        };
      });
    });
    setData(empty);
  };

  // ── Fetch reports list (includes client_id for filtering) ─────────────────
  const fetchReports = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports`, { withCredentials: true });
      setReportsList(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load reports');
    }
  };



  // ── Load report ───────────────────────────────────────────────────────────
  const loadReport = async (id) => {
    if (!id) { setReportId(null); setReportName(''); initEmptyData(); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hospital-reports/${id}`, { withCredentials: true });
      setReportId(id);
      setReportName(res.data.report_name);
      fetchFYPercentages(id);

      // ── Auto-link from Hospital Report data ───────────────────────────────
      const pharmProfit = parseFloat(res.data.pharmacy_profit_perc) || 0;
      const xrayProfit = parseFloat(res.data.xray_profit_perc) || 0;

      const financial = res.data.financial_data || {};

      const otherExpRows = res.data.other_expenses || [];

      const merged = {};

      let struct = [...initialStructure];
      let mains = [...mainSections];
      let cats = { ...categoriesInSection };

      // Load client's template if applicable
      const clientId = res.data.client_id;
      if (clientId) {
        try {
          const clientsRes = await axios.get(process.env.REACT_APP_API_URL + '/api/clients', { withCredentials: true });
          const client = clientsRes.data.find(c => c.id === clientId);
          let tId = client ? client.template_id : null;
          if (client && !tId && client.template_ids) {
            let ids = typeof client.template_ids === 'string' ? JSON.parse(client.template_ids) : client.template_ids;
            if (ids && ids.length > 0) {
              const { data: allTemplates } = await axios.get(process.env.REACT_APP_API_URL + '/api/templates?include_custom=true', { withCredentials: true });
              const customIds = ids.filter(id => allTemplates.some(t => t.id === Number(id) && t.is_custom));
              const targetIds = customIds.length > 0 ? customIds : ids;
              
              const responses = await Promise.all(targetIds.map(id => axios.get(process.env.REACT_APP_API_URL + `/api/templates/${id}/sections`, { withCredentials: true })));
              let mergedSections = [];
              responses.forEach(res => {
                if (res.data && res.data.length > 0) mergedSections = mergedSections.concat(res.data);
              });
              const { data: categories } = await axios.get(process.env.REACT_APP_API_URL + '/api/categories');
              
              if (mergedSections.length > 0) {
                struct = mergedSections.map(s => {
                  let parsedItems = typeof s.items === 'string' ? JSON.parse(s.items) : (s.items || []);
                  parsedItems = parsedItems.map(item => {
                    if (typeof item === 'number' || !isNaN(Number(item))) {
                      const cat = categories.find(c => c.id === Number(item));
                      return { id: Number(item), name: cat ? cat.name : item };
                    }
                    return { id: null, name: item };
                  });
                  return {
                    ref_id: s.ref_id,
                    main: s.main_section,
                    category: s.category,
                    title: s.sub_category,
                    main_id: s.main_section_id,
                    category_id: s.category_id,
                    sub_category_id: s.sub_category_id,
                    items: parsedItems
                  };
                });
                mains = [...new Set(mergedSections.map(s => s.main_section))];
                cats = {};
                mains.forEach(m => {
                  cats[m] = [...new Set(mergedSections.filter(s => s.main_section === m).map(s => s.category))];
                });
                setInitialStructure(struct);
                setMainSections(mains);
                setCategoriesInSection(cats);
                setOpenSections(Object.fromEntries(mains.map(m => [m, true])));
              }
            }
          }
        } catch (e) {
          console.error("Failed to load client template", e);
        }
      }

      // Deduce structure from saved data if no template is applied
      if (struct.length === 0 && Object.keys(financial).length > 0) {
        for (const ref in financial) {
          const rws = financial[ref];
          if (rws.length > 0) {
            const firstRow = rws[0];
            struct.push({
              ref_id: ref,
              main: firstRow.main_section || 'UNKNOWN',
              category: firstRow.category || 'Unknown',
              title: firstRow.sub_category || 'Unknown',
              main_id: firstRow.main_section_id || null,
              category_id: firstRow.category_id || null,
              sub_category_id: firstRow.sub_category_id || null,
              items: []
            });
            if (!mains.includes(firstRow.main_section)) mains.push(firstRow.main_section || 'UNKNOWN');
            if (!cats[firstRow.main_section]) cats[firstRow.main_section] = [];
            if (!cats[firstRow.main_section].includes(firstRow.category)) cats[firstRow.main_section].push(firstRow.category || 'Unknown');
          }
        }
        setInitialStructure(struct);
        setMainSections(mains);
        setCategoriesInSection(cats);
        setOpenSections(Object.fromEntries(mains.map(m => [m, true])));
      }

      // If struct is STILL empty (e.g. new report without a template), use DEFAULT_STRUCTURE
      if (struct.length === 0) {
        struct = DEFAULT_STRUCTURE;
        mains = [...new Set(struct.map(s => s.main))];
        cats = {};
        mains.forEach(m => {
          cats[m] = [...new Set(struct.filter(s => s.main === m).map(s => s.category))];
        });
        setInitialStructure(struct);
        setMainSections(mains);
        setCategoriesInSection(cats);
        setOpenSections(Object.fromEntries(mains.map(m => [m, true])));
      }

      struct.forEach(sec => {
        const savedRows = financial[sec.ref_id] || [];
        const subUpper = (sec.title || '').toUpperCase();

        const getProfitAmount = (amt) => {
          let a = parseFloat(amt) || 0;
          if (subUpper.includes('PHARMACY')) {
            a = a * (pharmProfit / 100);
          } else if (subUpper.includes('X-RAY') || subUpper.includes('X RAY') || subUpper.includes('XRAY')) {
            a = a * (xrayProfit / 100);
          }
          return a;
        };

        const rows = sec.items.map(staticItemObj => {
          let amount = 0;
          const staticItemName = typeof staticItemObj === 'string' ? staticItemObj : staticItemObj.name;
          const staticItemId = typeof staticItemObj === 'object' ? staticItemObj.id : null;

          const found = savedRows.find(r => r.item_name === staticItemName);
          if (found) {
            amount = getProfitAmount(found.amount);
          }

          return {
            item_name: staticItemName,
            item_id: staticItemId,
            amount,
            is_static: true,
            main_section: sec.main,
            category: sec.category,
            sub_category: sec.title,
            main_section_id: sec.main_id || null,
            category_id: sec.category_id || null,
            sub_category_id: sec.sub_category_id || null
          };
        });

        const staticNames = new Set(rows.map(r => r.item_name));

        // Dynamic rows
        if (sec.ref_id === 'other_direct_expenses') {
          const addedDesc = new Set(staticNames);
          otherExpRows.forEach(dyn => {
            if (!addedDesc.has(dyn.description)) {
              addedDesc.add(dyn.description);
              rows.push({ item_name: dyn.description, item_id: null, amount: parseFloat(dyn.per_annum) || 0, is_static: false, main_section: sec.main, category: sec.category, sub_category: sec.title, main_section_id: sec.main_id || null, category_id: sec.category_id || null, sub_category_id: sec.sub_category_id || null });
            }
          });
          savedRows.filter(r => !r.is_static).forEach(dyn => {
            if (!addedDesc.has(dyn.item_name)) {
              addedDesc.add(dyn.item_name);
              rows.push({ item_name: dyn.item_name, item_id: dyn.item_id || null, amount: getProfitAmount(dyn.amount), is_static: false, main_section: sec.main, category: sec.category, sub_category: sec.title, main_section_id: sec.main_id || null, category_id: sec.category_id || null, sub_category_id: sec.sub_category_id || null });
            }
          });
        } else {
          savedRows.filter(r => !r.is_static).forEach(dyn => {
            if (!staticNames.has(dyn.item_name)) {
              staticNames.add(dyn.item_name);
              rows.push({ item_name: dyn.item_name, item_id: dyn.item_id || null, amount: getProfitAmount(dyn.amount), is_static: false, main_section: sec.main, category: sec.category, sub_category: sec.title, main_section_id: sec.main_id || null, category_id: sec.category_id || null, sub_category_id: sec.sub_category_id || null });
            }
          });
        }

        merged[sec.ref_id] = rows;
      });

      setData(merged);
      toast.success('Report loaded!');
    } catch (err) {
      console.error(err);
      toast.error('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveReport = async () => {
    if (!reportId) return toast.error('Select a report first');
    setSaving(true);
    try {
      for (const sec of initialStructure) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/hospital-reports/${reportId}/financial/${sec.ref_id}`,
          { rows: data[sec.ref_id] },
          { withCredentials: true }
        );
      }
      toast.success('Financial Statements Saved!');
    } catch (err) {
      console.error(err);
      toast.error('Error saving');
    } finally {
      setSaving(false);
    }
  };

  // ── Row manipulation ──────────────────────────────────────────────────────
  // const updateRow = (ref_id, index, field, value) => {
  //   setData(prev => {
  //     const copy = { ...prev, [ref_id]: [...prev[ref_id]] };
  //     copy[ref_id][index] = { ...copy[ref_id][index], [field]: field === 'amount' ? (parseFloat(value) || 0) : value };
  //     return copy;
  //   });
  // };

  // const addRow = (ref_id) => {
  //   const sec = initialStructure.find(s => s.ref_id === ref_id);
  //   setData(prev => ({
  //     ...prev,
  //     [ref_id]: [...prev[ref_id], {
  //       item_name: '',
  //       item_id: null,
  //       amount: 0,
  //       is_static: false,
  //       main_section: sec ? sec.main : '',
  //       category: sec ? sec.category : '',
  //       sub_category: sec ? sec.title : '',
  //       main_section_id: sec ? (sec.main_id || null) : null,
  //       category_id: sec ? (sec.category_id || null) : null,
  //       sub_category_id: sec ? (sec.sub_category_id || null) : null
  //     }]
  //   }));
  // };

  // const removeRow = (ref_id, index) => {
  //   setData(prev => ({ ...prev, [ref_id]: prev[ref_id].filter((_, i) => i !== index) }));
  // };

  // ── Calculations ──────────────────────────────────────────────────────────
  const calcTotal = (ref_id) => (data[ref_id] || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const calcCatTotal = (mainSec, cat) => initialStructure.filter(s => s.main === mainSec && s.category === cat).reduce((s, sec) => s + calcTotal(sec.ref_id), 0);

  // Section I
  const totalIncome = calcCatTotal('INCOME AND EXPENDITURE', 'Income');
  const totalExpenditure = calcCatTotal('INCOME AND EXPENDITURE', 'Expenditure');
  const netProfitLoss = totalIncome - totalExpenditure;
  // Section II
  const totalLiabilities = calcCatTotal('BALANCE SHEET', 'Capital and Liabilities');
  const totalAssets = calcCatTotal('BALANCE SHEET', 'Properties and Assets');
  // Section III
  const totalDeprBuildings = calcTotal('depr_buildings');
  const totalDeprEquipment = calcTotal('depr_equipment');
  const totalDeprComputers = calcTotal('depr_computers');
  const totalDepreciation = totalDeprBuildings + totalDeprEquipment + totalDeprComputers;
  // Section IV
  const totalCashInflow = calcTotal('cash_inflow');
  const totalCashOutflow = calcTotal('cash_outflow');
  const netCashFlow = totalCashInflow - totalCashOutflow;
  // Section V
  const totalCashAccruals = calcTotal('dscr_cash_accruals');
  const totalObligations = calcTotal('dscr_obligations');
  const dscrRatio = totalObligations > 0 ? (totalCashAccruals / totalObligations).toFixed(2) : '∞';

  // ── Export to Excel ───────────────────────────────────────────────────────
  const exportToExcel = () => {
    if (!reportId) return toast.error('Select a report first');

    // Styling helper
    const cell = (v, s) => ({ v, s, t: typeof v === 'number' ? 'n' : 's' });

    // Define styles
    const styles = {
      title: { font: { bold: true, sz: 16 } },
      secHeader: (colorHex) => ({
        font: { bold: true, sz: 12, color: { rgb: 'FFFFFFFF' } },
        fill: { fgColor: { rgb: colorHex } }
      }),
      catHeader: {
        font: { bold: true, sz: 11, color: { rgb: 'FF1E293B' } },
        fill: { fgColor: { rgb: 'FFE2E8F0' } }
      },
      subCatHeader: {
        font: { bold: true, sz: 10, color: { rgb: 'FFB45309' } },
        fill: { fgColor: { rgb: 'FFFDF2E9' } }
      },
      text: { font: { color: { rgb: 'FF1E293B' } } },
      number: { font: { color: { rgb: 'FF1E293B' } }, alignment: { horizontal: 'right' } },
      sectionSummaryText: (colorHex) => ({
        font: { bold: true, sz: 11, color: { rgb: 'FFFFFFFF' } },
        fill: { fgColor: { rgb: colorHex } }
      }),
      sectionSummaryNumber: (colorHex) => ({
        font: { bold: true, sz: 11, color: { rgb: 'FFFFFFFF' } },
        fill: { fgColor: { rgb: colorHex } },
        alignment: { horizontal: 'right' }
      })
    };

    const wsData = [
      [cell(`FINANCIAL STATEMENT: ${reportName}`, styles.title)],
      [],
    ];

    // Map section names to hex colors
    const themeHex = {
      'INCOME AND EXPENDITURE': 'FF059669', // Emerald
      'BALANCE SHEET': 'FF0891B2',          // Cyan
      'DEPRECIATION SCHEDULE': 'FF7C3AED',  // Violet
      'CASH FLOW STATEMENTS': 'FFD97706',   // Amber
      'DEBT SERVICE COVERAGE RATIO': 'FFE11D48' // Rose
    };

    // Iterate sections
    mainSections.forEach(mainSec => {
      const hex = themeHex[mainSec] || 'FF475569';

      // Section Header
      wsData.push([cell(mainSec, styles.secHeader(hex)), cell('', styles.secHeader(hex))]);

      // Categories inside this section
      const cats = categoriesInSection[mainSec] || [];
      cats.forEach(cat => {
        const catTotal = calcCatTotal(mainSec, cat);
        wsData.push([
          cell(`  CATEGORY: ${cat.toUpperCase()}`, styles.catHeader),
          cell(catTotal, styles.catHeader)
        ]);

        // Sub-categories
        const subCats = initialStructure.filter(s => s.main === mainSec && s.category === cat);
        subCats.forEach(sec => {
          wsData.push([
            cell(`    ${sec.title}`, styles.subCatHeader),
            cell(calcTotal(sec.ref_id), styles.subCatHeader)
          ]);

          // Items
          const items = data[sec.ref_id] || [];
          items.forEach(row => {
            wsData.push([
              cell(`      ${row.item_name}`, styles.text),
              cell(row.amount || 0, styles.number)
            ]);
          });
        });
      });

      // Specific section summaries
      if (mainSec === 'INCOME AND EXPENDITURE') {
        wsData.push([
          cell('NET PROFIT / (LOSS)', styles.sectionSummaryText(hex)),
          cell(netProfitLoss, styles.sectionSummaryNumber(hex))
        ]);
      } else if (mainSec === 'BALANCE SHEET') {
        wsData.push([
          cell('BALANCE SHEET TALLY DIFFERENCE', styles.sectionSummaryText(hex)),
          cell(Math.abs(totalLiabilities - totalAssets), styles.sectionSummaryNumber(hex))
        ]);
      } else if (mainSec === 'DEPRECIATION SCHEDULE') {
        wsData.push([
          cell('TOTAL DEPRECIATION', styles.sectionSummaryText(hex)),
          cell(totalDepreciation, styles.sectionSummaryNumber(hex))
        ]);
      } else if (mainSec === 'CASH FLOW STATEMENTS') {
        wsData.push([
          cell('NET CASH FLOW', styles.sectionSummaryText(hex)),
          cell(netCashFlow, styles.sectionSummaryNumber(hex))
        ]);
      } else if (mainSec === 'DEBT SERVICE COVERAGE RATIO') {
        wsData.push([
          cell('DSCR RATIO', styles.sectionSummaryText(hex)),
          cell(typeof dscrRatio === 'string' ? dscrRatio : parseFloat(dscrRatio) || 0, styles.sectionSummaryNumber(hex))
        ]);
      }
      wsData.push([]); // empty line spacer
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 50 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financial Statements');
    XLSX.writeFile(wb, `${reportName.replace(/\s+/g, '_')}_Financial_Statement.xlsx`);
    toast.success('Excel Exported!');
  };

  // ── Export to PDF ─────────────────────────────────────────────────────────
  const exportToPDF = () => {
    if (!reportId) return toast.error('Select a report first');

    const doc = new jsPDF();
    let y = 15;

    doc.setFontSize(16);
    doc.text(`Financial Statement: ${reportName}`, 14, y);
    y += 10;

    const themeColors = {
      'INCOME AND EXPENDITURE': [5, 150, 105],       // Emerald
      'BALANCE SHEET': [8, 145, 178],                // Cyan
      'DEPRECIATION SCHEDULE': [124, 58, 237],        // Violet
      'CASH FLOW STATEMENTS': [217, 119, 6],         // Amber
      'DEBT SERVICE COVERAGE RATIO': [225, 29, 72]   // Rose
    };

    mainSections.forEach(mainSec => {
      const color = themeColors[mainSec] || [71, 85, 105];
      const body = [];

      const cats = categoriesInSection[mainSec] || [];
      cats.forEach(cat => {
        // eslint-disable-next-line no-unused-vars
        const catTotal = calcCatTotal(mainSec, cat);
        body.push([
          { content: `CATEGORY: ${cat.toUpperCase()}`, colSpan: 2, styles: { fillColor: [241, 245, 249], fontStyle: 'bold' } }
        ]);

        const subCats = initialStructure.filter(s => s.main === mainSec && s.category === cat);
        subCats.forEach(sec => {
          body.push([
            { content: `  ${sec.title}`, styles: { fontStyle: 'bold', fillColor: [254, 243, 199] } },
            { content: `Rs. ${fmt(calcTotal(sec.ref_id))}`, styles: { fontStyle: 'bold', halign: 'right', fillColor: [254, 243, 199] } }
          ]);

          const items = data[sec.ref_id] || [];
          items.forEach(row => {
            body.push([
              `    ${row.item_name}`,
              { content: `Rs. ${fmt(row.amount)}`, styles: { halign: 'right' } }
            ]);
          });
        });
      });

      let summaryLabel = '';
      let summaryVal = '';
      if (mainSec === 'INCOME AND EXPENDITURE') {
        summaryLabel = 'NET PROFIT / (LOSS)';
        summaryVal = `Rs. ${fmt(netProfitLoss)}`;
      } else if (mainSec === 'BALANCE SHEET') {
        summaryLabel = 'BALANCE SHEET TALLY DIFFERENCE';
        summaryVal = `Rs. ${fmt(Math.abs(totalLiabilities - totalAssets))}`;
      } else if (mainSec === 'DEPRECIATION SCHEDULE') {
        summaryLabel = 'TOTAL DEPRECIATION';
        summaryVal = `Rs. ${fmt(totalDepreciation)}`;
      } else if (mainSec === 'CASH FLOW STATEMENTS') {
        summaryLabel = 'NET CASH FLOW';
        summaryVal = `Rs. ${fmt(netCashFlow)}`;
      } else if (mainSec === 'DEBT SERVICE COVERAGE RATIO') {
        summaryLabel = 'DSCR RATIO';
        summaryVal = dscrRatio;
      }

      if (summaryLabel) {
        body.push([
          { content: summaryLabel, styles: { fillColor: color, textColor: [255, 255, 255], fontStyle: 'bold' } },
          { content: summaryVal, styles: { fillColor: color, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'right' } }
        ]);
      }

      doc.setFontSize(12);
      doc.text(mainSec, 14, y);

      autoTable(doc, {
        startY: y + 3,
        head: [['Item / Description', 'Amount']],
        body: body,
        theme: 'grid',
        headStyles: { fillColor: color },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 50 }
        }
      });

      y = doc.lastAutoTable.finalY + 10;
      if (y > 260) {
        doc.addPage();
        y = 15;
      }
    });

    doc.save(`${reportName.replace(/\s+/g, '_')}_Financial_Statement.pdf`);
    toast.success('PDF Exported!');
  };

  // ── Section toggle ────────────────────────────────────────────────────────
  const toggleSection = (s) => setOpenSections(prev => ({ ...prev, [s]: !prev[s] }));

  // ─────────────────────────────────────────────────────────────────────────
  // ── Row renderer ─────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  const renderRows = (ref_id, focusClass) => (
    <div className="space-y-1.5">
      {(data[ref_id] || []).map((row, i) => (
        <div
          key={i}
          className="flex gap-2 items-center rounded-lg px-2 py-1.5 transition-all bg-slate-900/20 border border-slate-800/50"
        >
          {/* Item name */}
          <input
            type="text"
            value={row.item_name}
            readOnly
            className="flex-1 bg-transparent border-0 text-sm outline-none transition py-0.5 px-1 rounded text-cyan-400 font-medium cursor-default select-none"
          />

          {/* Amount input */}
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">₹</span>
            <input
              type="text"
              value={row.amount === 0 ? '0.00' : fmt(row.amount)}
              readOnly
              className="w-40 pl-6 pr-3 py-1.5 rounded-lg text-right text-sm font-mono outline-none transition bg-slate-950/50 border border-slate-800 text-gray-300 cursor-not-allowed"
            />
          </div>
        </div>
      ))}
    </div>
  );

  // ── Category card ─────────────────────────────────────────────────────────
  const renderCategoryCard = (label, mainSec, cat, refs, focusClass) => {
    const catColors = CATEGORY_COLORS[cat] || CATEGORY_COLORS['Income'];
    const catTotal = calcCatTotal(mainSec, cat);

    return (
      <div key={cat} className="space-y-3">
        {/* 🟢 GREEN: Category header with total */}
        <div className={`rounded-xl p-3 flex justify-between items-center border ${catColors.header}`}>
          <h3 className="font-bold text-sm uppercase tracking-wide">{label || cat}</h3>
          <span className={`text-lg font-black font-mono ${catColors.total}`}>₹ {fmt(catTotal)}</span>
        </div>

        {/* Sub-sections */}
        {refs.map(sec => (
          <div key={sec.ref_id} className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
            {/* 🟡 YELLOW: Sub-title */}
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-semibold text-cyan-400 text-xs uppercase tracking-wide">{sec.title}</h4>
              <span className="text-xs font-bold text-gray-300 font-mono">₹ {fmt(calcTotal(sec.ref_id))}</span>
            </div>
            {/* ⬜ WHITE: Input rows */}
            {renderRows(sec.ref_id, catColors.focus)}
          </div>
        ))}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  const innerContent = (
    <>
      <Toaster position="top-right" />

      {/* ── TAB SWITCHER ─────────────────────────────────────────────────── */}
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setActiveTab('financial')}
          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition border flex items-center gap-2 ${activeTab === 'financial'
            ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.2)]'
            : 'bg-slate-900/60 border-slate-700 text-gray-400 hover:text-white hover:border-slate-500'
            }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          Financial Data
        </button>
        <button
          onClick={() => setActiveTab('percentage')}
          className={`px-5 py-2.5 rounded-xl font-bold text-sm transition border flex items-center gap-2 ${activeTab === 'percentage'
            ? 'bg-violet-500/20 border-violet-500/50 text-violet-300 shadow-[0_0_12px_rgba(139,92,246,0.2)]'
            : 'bg-slate-900/60 border-slate-700 text-gray-400 hover:text-white hover:border-slate-500'
            }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5l7 7-7 7-5-5V3z" /></svg>
          Financial Year %
          {fyPercentages.length > 0 && (
            <span className="ml-1 bg-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full font-bold">
              {fyPercentages.length}
            </span>
          )}
        </button>
      </div>

      {/* ── CONTROL BAR (shown in both tabs) ─────────────────────────────── */}
      {!isEmbedded && (
        <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-5 flex flex-wrap gap-4 items-end mb-6 backdrop-blur-sm">

          {/* CLIENT FILTER — only template-assigned clients show here */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">
              Filter by Client
            </label>
            <select
              className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-violet-500 transition"
              value={selectedClientId}
              onChange={e => {
                setSelectedClientId(e.target.value);
                setReportId(null);
                setReportName('');
              }}
            >
              <option value="">-- All Clients --</option>
              {clientsList.map(c => (
                <option key={c.id} value={c.id}>{c.client_name}</option>
              ))}
            </select>
            {clientsList.length === 0 && (
              <p className="text-xs text-amber-400 mt-1">⚠ Assign templates to clients first.</p>
            )}
          </div>

          {/* REPORT SELECT (filtered by selected client) */}
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs text-gray-400 font-semibold mb-1.5 uppercase tracking-widest">Select Project Report</label>
            <select
              className="w-full bg-slate-950 border border-slate-700 p-2.5 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition"
              value={reportId || ''}
              onChange={e => loadReport(e.target.value)}
            >
              <option value="">-- Select Existing Report --</option>
              {(selectedClientId
                ? reportsList.filter(r => String(r.client_id) === String(selectedClientId))
                : reportsList
              ).map(r => <option key={r.id} value={r.id}>{r.report_name}</option>)}
            </select>
          </div>

          {reportId && (
            <>
              {/* <div className="flex items-center gap-2 text-xs text-gray-400 bg-slate-800 px-3 py-2 rounded-xl border border-slate-700">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                {reportName}
              </div> */}
              <button
                onClick={exportToExcel}
                className="bg-green-700/20 hover:bg-green-700/40 text-green-400 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-green-700/30 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Export XLS
              </button>
              <button
                onClick={exportToPDF}
                className="bg-rose-700/20 hover:bg-rose-700/40 text-rose-400 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-rose-700/30 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Export PDF
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 px-4 py-2.5 rounded-xl font-bold text-sm transition border border-amber-600/30 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3 3 3m-3-3v12" /></svg> Reset
              </button>
            </>
          )}

          <button
            onClick={saveReport}
            disabled={saving || !reportId}
            className="bg-gradient-to-r from-emerald-500 to-teal-400 text-black px-6 py-2.5 rounded-xl font-bold text-sm transition hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-40 flex items-center gap-2"
          >
            {saving ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg> Saving...</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg> Save Statements</>
            )}
          </button>
        </div>
      )}

      {isEmbedded && reportId && (
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
          <h3 className="text-white font-bold text-base">🏛️ Financial Statement View</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={exportToExcel}
              className="bg-green-700/20 hover:bg-green-700/40 text-green-400 px-4 py-2 rounded-xl font-bold text-xs transition border border-green-700/30 flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Export XLS
            </button>
            <button
              onClick={exportToPDF}
              className="bg-rose-700/20 hover:bg-rose-700/40 text-rose-400 px-4 py-2 rounded-xl font-bold text-xs transition border border-rose-700/30 flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg> Export PDF
            </button>
            <button
              onClick={saveReport}
              disabled={saving}
              className="bg-gradient-to-r from-emerald-500 to-teal-400 text-black px-5 py-2 rounded-xl font-bold text-xs transition hover:shadow-lg hover:shadow-emerald-500/25 disabled:opacity-40 flex items-center gap-1.5"
            >
              {saving ? 'Saving...' : 'Save Statements'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'financial' && loading && (
        <div className="text-center py-20">
          <svg className="w-10 h-10 text-emerald-400 animate-spin mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-emerald-400 font-bold">Loading report data...</p>
        </div>
      )}

      {activeTab === 'financial' && !loading && (
        <div className="space-y-6 pb-24">
          {/* ════════════════════════════════════════════════════════════════ */}
          {/* DYNAMIC SECTIONS RENDERING                                       */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {mainSections.map((mainSec, idx) => {
            const theme = SECTION_THEMES[mainSec] || { icon: idx + 1, border: 'border-slate-500/40', bg: 'bg-slate-950', headerBg: 'bg-slate-500/15', iconBg: 'bg-slate-500/20', iconText: 'text-slate-400', titleText: 'text-slate-300' };

            // Subtitle logic
            let subtitle = null;
            if (mainSec === 'INCOME AND EXPENDITURE') {
              subtitle = <p className="text-xs text-gray-500">Net: <span className={netProfitLoss >= 0 ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>₹ {fmt(Math.abs(netProfitLoss))} {netProfitLoss >= 0 ? 'Profit' : 'Loss'}</span></p>;
            } else if (mainSec === 'BALANCE SHEET') {
              subtitle = <p className="text-xs text-gray-500">Tally: <span className={totalLiabilities === totalAssets ? 'text-cyan-400 font-bold' : 'text-orange-400 font-bold'}>{totalLiabilities === totalAssets ? '✓ Tallied' : `Diff ₹ ${fmt(Math.abs(totalLiabilities - totalAssets))}`}</span></p>;
            } else if (mainSec === 'DEPRECIATION SCHEDULE') {
              subtitle = <p className="text-xs text-gray-500">Total Depreciation: <span className="text-violet-400 font-bold">₹ {fmt(totalDepreciation)}</span></p>;
            } else if (mainSec === 'CASH FLOW STATEMENTS') {
              subtitle = <p className="text-xs text-gray-500">Net Cash Flow: <span className={netCashFlow >= 0 ? 'text-green-400 font-bold' : 'text-rose-400 font-bold'}>₹ {fmt(Math.abs(netCashFlow))} {netCashFlow >= 0 ? 'Surplus' : 'Deficit'}</span></p>;
            } else if (mainSec === 'DEBT SERVICE COVERAGE RATIO') {
              const dscrNum = parseFloat(dscrRatio);
              const dscrGood = !isNaN(dscrNum) && dscrNum >= 1.25;
              subtitle = <p className="text-xs text-gray-500">DSCR: <span className={dscrGood ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>{dscrRatio} {dscrGood ? '✓ Good (≥1.25)' : '⚠ Below Threshold'}</span></p>;
            }

            return (
              <div key={mainSec} className={`rounded-3xl border ${theme.border} overflow-hidden shadow-2xl`}>
                <button
                  onClick={() => toggleSection(mainSec)}
                  className={`w-full ${theme.headerBg} px-6 py-4 flex items-center justify-between transition hover:bg-slate-500/20`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-9 h-9 rounded-xl ${theme.iconBg} flex items-center justify-center ${theme.iconText} font-black text-sm border ${theme.border}`}>{theme.icon}</span>
                    <div className="text-left">
                      <h2 className={`font-black text-lg ${theme.titleText}`}>{mainSec}</h2>
                      {subtitle}
                    </div>
                  </div>
                  <svg className={`w-5 h-5 ${theme.iconText} transition-transform duration-300 ${openSections[mainSec] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                </button>

                {openSections[mainSec] && (
                  <div className="bg-slate-950 p-6">
                    {!reportId ? (
                      <div className="text-center py-10 text-gray-500">Select a report to view data</div>
                    ) : (
                      <>
                        <div className={`grid grid-cols-1 ${mainSec === 'DEPRECIATION SCHEDULE' ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6`}>
                          {(categoriesInSection[mainSec] || []).map(cat =>
                            renderCategoryCard(
                              cat,
                              mainSec,
                              cat,
                              initialStructure.filter(s => s.main === mainSec && s.category === cat),
                              "focus:border-emerald-500"
                            )
                          )}
                        </div>

                        {/* SPECIFIC SUMMARIES */}
                        {mainSec === 'INCOME AND EXPENDITURE' && (
                          <div className={`mt-6 p-5 rounded-2xl flex justify-between items-center border ${netProfitLoss >= 0 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}>
                            <div>
                              <h3 className="font-black text-xl text-white">NET {netProfitLoss >= 0 ? 'PROFIT' : 'LOSS'}</h3>
                              <p className="text-xs text-gray-500">Income − Expenditure</p>
                            </div>
                            <span className={`text-3xl font-black font-mono ${netProfitLoss >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                              ₹ {fmt(Math.abs(netProfitLoss))}
                            </span>
                          </div>
                        )}

                        {mainSec === 'BALANCE SHEET' && (
                          <div className={`mt-6 p-5 rounded-2xl flex justify-between items-center border ${totalLiabilities === totalAssets ? 'bg-cyan-500/10 border-cyan-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
                            <div>
                              <h3 className="font-black text-xl text-white">TALLY STATUS</h3>
                              <p className="text-xs text-gray-500">Liabilities must equal Assets</p>
                            </div>
                            <div className="text-right">
                              <div className={`text-2xl font-black ${totalLiabilities === totalAssets ? 'text-cyan-400' : 'text-orange-400'}`}>
                                {totalLiabilities === totalAssets ? '✓ TALLIED' : 'MISMATCH'}
                              </div>
                              {totalLiabilities !== totalAssets && (
                                <p className="text-xs text-orange-300 font-bold mt-1">Diff: ₹ {fmt(Math.abs(totalLiabilities - totalAssets))}</p>
                              )}
                            </div>
                          </div>
                        )}

                        {mainSec === 'CASH FLOW STATEMENTS' && (
                          <div className={`mt-6 p-5 rounded-2xl flex justify-between items-center border ${netCashFlow >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}>
                            <div>
                              <h3 className="font-black text-xl text-white">NET CASH FLOW</h3>
                              <p className="text-xs text-gray-500">Inflow − Outflow</p>
                            </div>
                            <span className={`text-3xl font-black font-mono ${netCashFlow >= 0 ? 'text-green-400' : 'text-rose-400'}`}>
                              ₹ {fmt(Math.abs(netCashFlow))}
                            </span>
                          </div>
                        )}

                        {mainSec === 'DEBT SERVICE COVERAGE RATIO' && (() => {
                          const dscrNum = parseFloat(dscrRatio);
                          const dscrGood = !isNaN(dscrNum) && dscrNum >= 1.25;
                          return (
                            <>
                              <div className={`mt-6 p-6 rounded-2xl border flex flex-col sm:flex-row items-center justify-between gap-4 ${dscrGood ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`}>
                                <div>
                                  <h3 className="font-black text-xl text-white">DSCR = Cash Accruals ÷ Obligations</h3>
                                  <p className="text-xs text-gray-500 mt-1">Benchmark: ≥ 1.25 is considered healthy by banks</p>
                                </div>
                                <div className="text-right">
                                  <div className={`text-5xl font-black font-mono ${dscrGood ? 'text-emerald-400' : 'text-rose-400'}`}>{dscrRatio}</div>
                                  <div className={`text-sm font-bold mt-1 ${dscrGood ? 'text-emerald-300' : 'text-rose-300'}`}>{dscrGood ? '✓ Eligible for Bank Loan' : '⚠ May face loan rejection'}</div>
                                </div>
                              </div>
                              <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="bg-teal-900/20 border border-teal-500/20 rounded-xl p-4 text-center">
                                  <p className="text-xs text-gray-500 mb-1">Total Cash Accruals</p>
                                  <p className="text-xl font-black text-teal-400 font-mono">₹ {fmt(totalCashAccruals)}</p>
                                </div>
                                <div className="bg-rose-900/20 border border-rose-500/20 rounded-xl p-4 text-center">
                                  <p className="text-xs text-gray-500 mb-1">Total Obligations</p>
                                  <p className="text-xl font-black text-rose-400 font-mono">₹ {fmt(totalObligations)}</p>
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── FY PERCENTAGE TAB CONTENT ─────────────────────────────────────── */}
      {activeTab === 'percentage' && (
        <div className="pb-24">
          {!reportId ? (
            <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/60 border border-slate-700 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-gray-400 font-bold text-lg">Select a Project Report first</p>
              <p className="text-gray-600 text-sm mt-1">Choose a project from the dropdown above to view its FY-wise percentages</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div>
                  <h2 className="text-xl font-black text-white">Financial Year Percentages</h2>
                  <p className="text-sm text-gray-400 mt-0.5">Project: <span className="text-violet-400 font-bold">{reportName}</span></p>
                </div>
                <a
                  href="/project-percentage"
                  className="bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-400 hover:to-indigo-400 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition shadow-lg shadow-violet-500/25 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Manage in Master
                </a>
              </div>

              {fyLoading ? (
                <div className="text-center py-10">
                  <svg className="w-8 h-8 text-violet-400 animate-spin mx-auto" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : (
                <>
                  {/* FY Tabs */}
                  {fyList.length > 0 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      <button
                        onClick={() => setSelectedFYTab('')}
                        className={`px-4 py-2 rounded-xl font-bold text-sm transition border ${selectedFYTab === ''
                          ? 'bg-violet-500/20 border-violet-500/50 text-violet-300'
                          : 'bg-slate-900/60 border-slate-700 text-gray-400 hover:text-white'
                          }`}
                      >
                        All Years
                      </button>
                      {fyList.map(fy => (
                        <button
                          key={fy}
                          onClick={() => setSelectedFYTab(fy)}
                          className={`px-4 py-2 rounded-xl font-bold text-sm transition border ${selectedFYTab === fy
                            ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300 shadow-[0_0_12px_rgba(6,182,212,0.2)]'
                            : 'bg-slate-900/60 border-slate-700 text-gray-400 hover:text-white'
                            }`}
                        >
                          FY {fy}
                          <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${selectedFYTab === fy ? 'bg-cyan-500/30 text-cyan-300' : 'bg-slate-700 text-gray-400'
                            }`}>
                            {fyPercentages.filter(r => r.financial_year === fy).length}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}

                  {fyDisplayed.length === 0 ? (
                    <div className="bg-slate-900/60 border border-slate-700 rounded-2xl p-12 text-center">
                      <div className="w-14 h-14 rounded-xl bg-slate-800/60 border border-slate-700 flex items-center justify-center mx-auto mb-3">
                        <svg className="w-7 h-7 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 7h.01M7 3h5l7 7-7 7-5-5V3z" />
                        </svg>
                      </div>
                      <p className="text-gray-400 font-semibold">No percentage records found</p>
                      <p className="text-gray-600 text-sm mt-1">Go to Project Percentage Master to add records for this project</p>
                    </div>
                  ) : (
                    <div className="bg-slate-900/60 border border-slate-700 rounded-2xl overflow-hidden">
                      <div className="p-4 border-b border-slate-700/50">
                        <p className="text-sm text-gray-400 font-medium">
                          {fyDisplayed.length} record{fyDisplayed.length !== 1 ? 's' : ''}
                          {selectedFYTab ? ` in FY ${selectedFYTab}` : ' across all financial years'}
                        </p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="bg-slate-800/60 border-b border-slate-700">
                              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">#</th>
                              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Financial Year</th>
                              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Percentage Label</th>
                              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-400">Value (%)</th>
                              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-emerald-400">Income Share</th>
                              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-rose-400">Expense Share</th>
                              <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-cyan-400">Net Share</th>
                              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Notes</th>
                              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Added On</th>
                              <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">Updated</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800/50">
                            {fyDisplayed.map((rec, idx) => {
                              const pctVal = parseFloat(rec.percentage_value) || 0;
                              const pctColor = pctVal >= 75 ? 'text-emerald-400' : pctVal >= 50 ? 'text-teal-400' : pctVal >= 25 ? 'text-amber-400' : 'text-rose-400';
                              const incShare = totalIncome * (pctVal / 100);
                              const expShare = totalExpenditure * (pctVal / 100);
                              const netShare = netProfitLoss * (pctVal / 100);
                              return (
                                <tr key={rec.id} className="hover:bg-slate-800/30 transition-colors">
                                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{idx + 1}</td>
                                  <td className="px-4 py-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/15 border border-cyan-500/30 text-cyan-400">
                                      FY {rec.financial_year}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-sm text-gray-200 font-medium">{rec.percentage_label}</span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <div className="w-16 bg-slate-800 rounded-full h-1.5 hidden sm:block">
                                        <div
                                          className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-400"
                                          style={{ width: `${Math.min(100, pctVal)}%` }}
                                        />
                                      </div>
                                      <span className={`text-base font-black font-mono ${pctColor}`}>{pctVal.toFixed(2)}%</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-400">
                                    ₹ {fmt(incShare)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono font-bold text-rose-400">
                                    ₹ {fmt(expShare)}
                                  </td>
                                  <td className="px-4 py-3 text-right font-mono font-bold text-cyan-400">
                                    ₹ {fmt(netShare)}
                                  </td>
                                  <td className="px-4 py-3 max-w-[160px]">
                                    <span className="text-xs text-gray-400">{rec.notes || '—'}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-xs text-gray-400">{fmtDate(rec.created_at)}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-xs text-gray-400">{fmtDate(rec.updated_at)}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

    </>
  );

  if (isEmbedded) {
    return <div className="embedded-financial-statement">{innerContent}</div>;
  }

  return (
    <Layout title="Financial Statements" subtitle="Income & Expenditure · Balance Sheet · Depreciation · Cash Flow · DSCR">
      {innerContent}
    </Layout>
  );
};

export default FinancialStatement;
